/**
 * Centralized animation variants for reveal animations.
 * 
 * IMPORTANT: These variants are designed to work with useRevealOnce hook
 * which prevents elements from disappearing after they've been revealed.
 * 
 * Never use whileInView="show" directly with these variants - always use
 * the useRevealOnce hook to ensure elements stay visible after reveal.
 */

// Seeded random number generator for deterministic per-index randomization
function seeded(i: number, k = 1) {
  const x = Math.sin(i * 12.9898 + k * 78.233) * 43758.5453;
  return x - Math.floor(x);
}

function randIndex(i: number, min: number, max: number, k = 1) {
  return min + seeded(i, k) * (max - min);
}

/**
 * Container variant for staggered grid animations.
 * Use with useRevealOnce hook to prevent re-hiding.
 */
export const gridContainerVariants = {
  hidden: {},
  show: {
    transition: { staggerChildren: 0.12, delayChildren: 0.05 },
  },
};

/**
 * Container variant for stats sections with simpler animations.
 * Use with useRevealOnce hook to prevent re-hiding.
 */
export const statsContainerVariants = {
  hidden: {},
  show: {
    transition: { staggerChildren: 0.12, delayChildren: 0.05 },
  },
};

/**
 * Item variant for stats sections (simple fade up).
 * Use with useRevealOnce hook to prevent re-hiding.
 */
export const statsItemVariants = {
  hidden: { opacity: 0, y: 32 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 1.1, ease: 'easeOut' as const },
  },
};

/**
 * Get randomized item variants for grid items.
 * Each index produces deterministic but varied animations.
 * Use with useRevealOnce hook to prevent re-hiding.
 */
export function getGridItemVariants(i: number) {
  const axisPick = seeded(i, 2);
  const offsetY = axisPick > 0.66 ? 0 : randIndex(i + 1, 14, 36, 3);
  const offsetX = axisPick < 0.33 ? 0 : randIndex(i + 2, -22, 22, 5);
  const rot = randIndex(i + 3, -1.5, 1.5, 7);
  const dur = randIndex(i + 4, 0.9, 1.25, 11);
  return {
    hidden: { opacity: 0, y: offsetY, x: offsetX, rotateZ: rot },
    show: {
      opacity: 1,
      y: 0,
      x: 0,
      rotateZ: 0,
      transition: { duration: dur, ease: 'easeOut' as const },
    },
  };
}

